import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Scale, Handshake } from "lucide-react";

const values = [
  { icon: Shield, title: "Transparency", text: "We tell you exactly what we can do, what we can't, and why. No smoke and mirrors — ever." },
  { icon: Scale, title: "Ethical Practice", text: "We operate within the law, every time. Disputing inaccurate information is your federally protected right." },
  { icon: Handshake, title: "Partnership", text: "We don't work for you — we work with you. Your goals become our mission until the job is done." },
];

const team = [
  { name: "Commander R. Hayes", role: "Founder & Lead Consultant",
    bio: "Former military officer turned financial advocate. After watching fellow veterans struggle with credit barriers, he founded Infantry Financial to bring discipline and strategy to credit restoration." },
  { name: "Alexis Monroe", role: "Senior Credit Strategist",
    bio: "With 10+ years in consumer finance and credit counseling, Alexis specializes in mortgage preparation and debt-to-income optimization for first-time homebuyers." },
  { name: "Jordan Pierce", role: "Dispute & Compliance Specialist",
    bio: "Jordan ensures every dispute letter, every communication, and every strategy is fully compliant with FCRA and CROA regulations — protecting clients at every step." },
];

export default function About() {
  return (
    <>
      <section className="hero-radial py-24 relative z-[2]">
        <div className="container-if">
          <div className="font-mono-label text-[var(--if-gold)] mb-3">Our Story</div>
          <h1 className="font-display text-[48px] md:text-[80px] leading-[0.95] max-w-4xl">
            Built on discipline. <br />
            Driven by <span className="italic text-[var(--if-gold)]">results</span>.
          </h1>
          <p className="mt-6 text-xl text-[rgba(35,38,28,0.7)] max-w-2xl">
            Infantry Financial was founded on a simple belief: every person deserves a fair shot at financial freedom — regardless of their past.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container-if grid md:grid-cols-2 gap-12 items-start">
          <div>
            <div className="font-mono-label text-[var(--if-gold)] mb-3">Our Mission</div>
            <h2 className="font-display text-[36px] md:text-[48px] leading-tight mb-6">Why Infantry Financial exists.</h2>
            <div className="flex flex-col gap-5 text-[rgba(27,33,26,0.8)] leading-relaxed">
              <p>The name <strong className="text-[var(--if-ink)]">Infantry Financial</strong> isn't an accident. Infantry soldiers are the ones who do the hard work on the ground — disciplined, strategic, and relentless. That's exactly how we approach credit restoration.</p>
              <p>Our founder, a former military officer, watched too many veterans and working families get denied for homes, cars, and business loans — not because they were bad people, but because the system is complicated and the industry is full of bad actors.</p>
              <p>We built Infantry Financial to be different. <strong className="text-[var(--if-ink)]">No vague promises. No shortcuts.</strong> Just a disciplined, transparent process that actually works — and a team that stays with you until the mission is complete.</p>
              <p>We're based in Texas and proud of it. We understand the Texas market, the Texas lenders, and the Texas families we serve.</p>
            </div>
            <Link to="/services" className="btn-outline mt-8" data-testid="about-see-services">
              See Our Services <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="card-dark p-10 relative overflow-hidden">
            <div className="font-display text-[82px] text-[var(--if-gold)] leading-none">8+</div>
            <div className="font-mono-label text-[rgba(35,38,28,0.7)] mt-2">Years Serving Texas Clients</div>
            <div className="divider-gold my-8" />
            <div className="font-display text-[82px] text-[var(--if-gold)] leading-none">500+</div>
            <div className="font-mono-label text-[rgba(35,38,28,0.7)] mt-2">Real People Helped</div>
            <div className="divider-gold my-8" />
            <div className="font-display text-[82px] text-[var(--if-gold)] leading-none">4.9★</div>
            <div className="font-mono-label text-[rgba(35,38,28,0.7)] mt-2">Client Rating</div>
          </div>
        </div>
      </section>

      <section className="section dark-scope bg-[#F5F4EF]">
        <div className="container-if">
          <div className="font-mono-label text-[var(--if-gold)] mb-3">What We Stand For</div>
          <h2 className="font-display text-[36px] md:text-[48px] mb-14">Our core values.</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v) => (
              <div key={v.title} className="card-dark p-8">
                <v.icon className="w-7 h-7 text-[var(--if-gold)] mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-2xl mb-3">{v.title}</h3>
                <p className="text-[rgba(35,38,28,0.7)] leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-if">
          <div className="font-mono-label text-[var(--if-gold)] mb-3">The Team</div>
          <h2 className="font-display text-[36px] md:text-[48px] mb-4">People you can trust.</h2>
          <p className="text-lg text-[rgba(27,33,26,0.65)] max-w-2xl mb-14">
            Real people with real expertise — not a call center, not a bot. When you work with Infantry Financial, you work with us directly.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {team.map((t) => (
              <div key={t.name} className="card-dark p-8">
                <div className="w-full aspect-[4/5] rounded-lg mb-5 bg-gradient-to-br from-[var(--if-olive)] to-[var(--if-olive-deep)] flex items-center justify-center">
                  <span className="font-display text-6xl text-[var(--if-gold)] opacity-40">
                    {t.name.split(" ").map(n => n[0]).join("").slice(0,2)}
                  </span>
                </div>
                <h3 className="font-display text-xl mb-1">{t.name}</h3>
                <div className="font-mono-label text-[var(--if-gold)] mb-3">{t.role}</div>
                <p className="text-sm text-[rgba(35,38,28,0.7)] leading-relaxed">{t.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section hero-radial">
        <div className="container-if text-center max-w-3xl mx-auto">
          <h2 className="font-display text-[40px] md:text-[56px]">Ready to start your mission?</h2>
          <p className="mt-4 text-lg text-[rgba(35,38,28,0.7)]">Free consultation. Real results.</p>
          <Link to="/booking" className="btn-gold mt-8" data-testid="about-cta">
            Book Free Consultation <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </>
  );
}
